import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { API } from "../services/api";
import { readStoredAuth, writeStoredAuth } from "../auth/authStorage";

const PASSWORD_RULES = [
    { key: "length",   label: "8 caractères minimum",         test: p => p.length >= 8 },
    { key: "upper",    label: "Une lettre majuscule",          test: p => /[A-Z]/.test(p) },
    { key: "digit",    label: "Un chiffre",                    test: p => /\d/.test(p) },
    { key: "special",  label: "Un caractère spécial (!@#...)", test: p => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
];

function usePasswordStrength(password) {
    return useMemo(() => {
        const results = PASSWORD_RULES.map(r => ({ ...r, ok: r.test(password) }));
        const score = results.filter(r => r.ok).length;
        return { results, score };
    }, [password]);
}

export default function ChangePasswordModal({ onClose }) {
    const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirm: "" });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showRules, setShowRules] = useState(false);

    const { results, score } = usePasswordStrength(form.newPassword);
    const allRulesOk = score === PASSWORD_RULES.length;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!allRulesOk) {
            setError("Le mot de passe ne respecte pas les critères de sécurité.");
            return;
        }
        if (form.newPassword !== form.confirm) {
            setError("Les nouveaux mots de passe ne correspondent pas.");
            return;
        }
        setSubmitting(true);
        try {
            const response = await API.put("/users/change-password", {
                currentPassword: form.currentPassword,
                newPassword: form.newPassword,
            }, { noRedirectOn401: true });
            // Le changement révoque l'ancienne session : le serveur pose un nouveau cookie et renvoie le profil
            // de session, dont l'expiration remplace celle gardée par le navigateur.
            const profile = response?.data;
            const auth = profile?.expiresAt ? readStoredAuth({ allowExpired: true }) : null;
            if (auth) writeStoredAuth({ ...auth, ...profile });
            setSuccess(true);
        } catch (err) {
            if (err.response?.status === 401) {
                setError("Session expirée. Veuillez vous reconnecter.");
            } else {
                setError(err.response?.data?.message ?? "Erreur lors du changement de mot de passe.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const strengthColor = ["#e0e0e0", "#f44336", "#ff9800", "#ffc107", "#4caf50"][score];
    const strengthLabel = ["", "Très faible", "Faible", "Moyen", "Fort"][score];

    return (
        <div style={overlayStyle}>
            <div style={modalStyle}>
                <h2 style={{ margin: "0 0 20px 0", fontSize: "18px", fontWeight: 700, color: "#1a1a1a" }}>
                    Changer le mot de passe
                </h2>

                {success ? (
                    <div>
                        <p style={{ color: "#2e7d32", marginBottom: "20px" }}>
                            Mot de passe modifié avec succès.
                        </p>
                        <button onClick={onClose} style={btnPrimaryStyle}>Fermer</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                        <div style={fieldStyle}>
                            <label htmlFor="change-password-current" style={labelStyle}>Mot de passe actuel *</label>
                            <input
                                id="change-password-current"
                                type="password"
                                style={inputStyle}
                                value={form.currentPassword}
                                onChange={e => setForm(f => ({ ...f, currentPassword: e.target.value }))}
                                required
                                autoFocus
                            />
                        </div>

                        <div style={fieldStyle}>
                            <label htmlFor="change-password-new" style={labelStyle}>Nouveau mot de passe *</label>
                            <input
                                id="change-password-new"
                                type="password"
                                style={inputStyle}
                                value={form.newPassword}
                                onChange={e => {
                                    setForm(f => ({ ...f, newPassword: e.target.value }));
                                    setShowRules(true);
                                }}
                                required
                            />
                            {showRules && form.newPassword.length > 0 && (
                                <div style={{ marginTop: "8px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                                        <div style={{ flex: 1, height: "4px", borderRadius: "2px", background: "#e0e0e0", overflow: "hidden" }}>
                                            <div style={{
                                                width: `${(score / PASSWORD_RULES.length) * 100}%`,
                                                height: "100%",
                                                background: strengthColor,
                                                transition: "width 0.3s, background 0.3s",
                                            }} />
                                        </div>
                                        <span style={{ fontSize: "11px", color: strengthColor, fontWeight: 600, minWidth: "60px" }}>
                                            {strengthLabel}
                                        </span>
                                    </div>
                                    <ul style={{ margin: 0, padding: "0 0 0 4px", listStyle: "none", display: "flex", flexDirection: "column", gap: "3px" }}>
                                        {results.map(r => (
                                            <li key={r.key} style={{ fontSize: "12px", color: r.ok ? "#2e7d32" : "#c62828", display: "flex", alignItems: "center", gap: "5px" }}>
                                                <span>{r.ok ? "✓" : "✗"}</span>
                                                {r.label}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div style={fieldStyle}>
                            <label htmlFor="change-password-confirm" style={labelStyle}>Confirmer le nouveau mot de passe *</label>
                            <input
                                id="change-password-confirm"
                                type="password"
                                style={{
                                    ...inputStyle,
                                    borderColor: form.confirm && form.confirm !== form.newPassword ? "#c62828" : "#ccc",
                                }}
                                value={form.confirm}
                                onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                                required
                            />
                            {form.confirm && form.confirm !== form.newPassword && (
                                <span style={{ fontSize: "12px", color: "#c62828" }}>Les mots de passe ne correspondent pas</span>
                            )}
                        </div>

                        {error && <p style={errorStyle}>{error}</p>}

                        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
                            <button type="button" onClick={onClose} style={btnSecondaryStyle}>Annuler</button>
                            <button
                                type="submit"
                                disabled={submitting || !allRulesOk || form.newPassword !== form.confirm}
                                style={{
                                    ...btnPrimaryStyle,
                                    opacity: (!allRulesOk || form.newPassword !== form.confirm) ? 0.5 : 1,
                                    cursor: (!allRulesOk || form.newPassword !== form.confirm) ? "not-allowed" : "pointer",
                                }}
                            >
                                {submitting ? "Enregistrement..." : "Enregistrer"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

const overlayStyle = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
};

const modalStyle = {
    background: "white",
    borderRadius: "8px",
    padding: "28px 32px",
    width: "380px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
};

const fieldStyle = { display: "flex", flexDirection: "column", gap: "4px" };
const labelStyle = { fontSize: "12px", fontWeight: 600, color: "#555" };
const inputStyle = {
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "14px",
};
const btnPrimaryStyle = {
    padding: "8px 18px",
    background: "#1976d2",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
};
const btnSecondaryStyle = {
    padding: "8px 18px",
    background: "transparent",
    color: "#555",
    border: "1px solid #ccc",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
};
const errorStyle = { color: "#c62828", fontSize: "13px", margin: 0 };

ChangePasswordModal.propTypes = {
    onClose: PropTypes.func.isRequired,
};
